import { useEffect, useState } from "react";
import { api, errorText } from "@/lib/api";
import { Field, Loading, PrimaryButton, SecondaryButton, errorCls, inputCls } from "@/components/agent/ui";
import { Checkbox } from "@/components/ui/checkbox";

const UnitInput = ({ unit, ...props }) => (
  <div className="relative">
    <input type="number" required className={`${inputCls} pr-20`} {...props} />
    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{unit}</span>
  </div>
);

export const PropertyForm = ({ onSaved, onCancel, submitLabel = "Add property" }) => {
  const [form, setForm] = useState({ address: "", monthly_rent: "", move_in_date: "", tenants: 1 });
  const [criteria, setCriteria] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/criteria/defaults").then((r) => setCriteria({ ...r.data, accept_guarantor: false })).catch((err) => setError(errorText(err)));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setC = (k) => (e) => setCriteria({ ...criteria, [k]: e.target.value === "" ? "" : Number(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/lets", { ...form, monthly_rent: Number(form.monthly_rent), tenants: Number(form.tenants), criteria });
      onSaved(data);
    } catch (err) {
      setError(errorText(err, "Please check the details and try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!criteria) return error ? <p className={errorCls}>{error}</p> : <Loading />;

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="new-let-form">
      <Field label="Address" help="Shown to applicants on the apply page.">
        <input autoFocus required minLength={3} value={form.address} onChange={set("address")} placeholder="Flat 1, 10 Example Road, London N1" className={inputCls} data-testid="new-let-address-input" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Move-in date">
          <input type="date" required value={form.move_in_date} onChange={set("move_in_date")} className={inputCls} data-testid="new-let-movein-input" />
        </Field>
        <Field label="Tenants">
          <input type="number" required min="1" max="10" value={form.tenants} onChange={set("tenants")} className={inputCls} data-testid="new-let-tenants-input" />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Monthly rent">
          <UnitInput unit="£ / month" min="1" step="1" value={form.monthly_rent} onChange={set("monthly_rent")} data-testid="new-let-rent-input" />
        </Field>
        <Field label="Minimum yearly income">
          <UnitInput unit="× rent" min="1" value={criteria.income_multiplier} onChange={setC("income_multiplier")} data-testid="criteria-income-multiplier" />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 sm:items-end" data-testid="criteria-guarantor">
        <div className={`transition-opacity duration-200 ${criteria.accept_guarantor ? "" : "opacity-40"}`}>
          <Field label="Guarantor's minimum yearly income">
            <UnitInput unit="× rent" min="1" value={criteria.guarantor_income_multiplier} onChange={setC("guarantor_income_multiplier")}
              disabled={!criteria.accept_guarantor} data-testid="criteria-guarantor-multiplier" />
          </Field>
        </div>
        <label className="flex h-9 w-fit cursor-pointer items-center gap-2 text-sm font-medium">
          <Checkbox checked={criteria.accept_guarantor} onCheckedChange={(v) => setCriteria({ ...criteria, accept_guarantor: v === true })} data-testid="criteria-accept-guarantor" />
          Accept a guarantor
        </label>
      </div>
      {error && <p className={errorCls} data-testid="new-let-error">{error}</p>}
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && <SecondaryButton onClick={onCancel} testid="new-let-cancel-button">Cancel</SecondaryButton>}
        <PrimaryButton type="submit" isLoading={busy} testid="new-let-submit-button">{submitLabel}</PrimaryButton>
      </div>
    </form>
  );
};
